import React, { useState, useCallback } from 'react';
import {
  Box,
  Flex,
  Heading,
  Badge,
  Text,
  VStack,
  HStack,
  IconButton,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { Undo2 } from 'lucide-react';
import type { CleanupAction } from './control-panel-dummy-data';
import { ActionsDetailModal } from './actions-detail-modal';
import { useActions } from '@/providers/actions-provider';
import { useStreamContext } from '@/providers/stream-provider';
import { useAppSelector } from '@/stores/store';
import { selectSelectedAgent } from '@/stores/selectors/agent-selectors';
import { appConfig } from '@/config/app-config';
import { STORAGE_KEYS } from '@/config/storage-config';



interface DeletedEntry {
  action: CleanupAction;
  index: number;
}

interface ActionsSectionProps {
  onActionsChange?: (count: number) => void;
}

export const ActionsSection: React.FC<ActionsSectionProps> = ({ onActionsChange }) => {
  const { actions, removeAction, addActions } = useActions();
  const { currentThreadId } = useStreamContext();
  const selectedAgent = useAppSelector(selectSelectedAgent);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [undoStack, setUndoStack] = useState<DeletedEntry[]>([]);

  const handleRemoveAction = useCallback((id: string) => {
    const index = actions.findIndex((a) => a.id === id);
    if (index !== -1) {
      setUndoStack((prev) => [...prev, { action: actions[index], index }]);
    }
    removeAction(id);
    onActionsChange?.(actions.length - 1);
  }, [actions, removeAction, onActionsChange]);

  const handleUndo = useCallback(async () => {
    if (undoStack.length === 0 || !currentThreadId) return;
    const { action } = undoStack[undoStack.length - 1];
    setUndoStack((prev) => prev.slice(0, -1));
    // PATCH the action back to pending on the server
    const agentId = selectedAgent?.id || 'optimize-clean-up-agent';
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN) || '';
    try {
      await fetch(
        `${appConfig.API_BASE_URL}/agents/${agentId}/threads/${currentThreadId}/actions/${action.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({ status: 'pending' }),
        },
      );
    } catch {
      // non-critical — restore in-memory even if server patch fails
    }
    addActions([action]);
    onActionsChange?.(actions.length + 1);
  }, [undoStack, actions.length, currentThreadId, selectedAgent?.id, addActions, onActionsChange]);

  const canUndo = undoStack.length > 0;

  const preview = actions.slice(0, 3);

  return (
    <>
      <Box>
        <Flex justify="space-between" align="center" mb={3}>
          <Heading size="sm" color="gray.700">
            Actions
          </Heading>
          <HStack spacing={1}>
            <Tooltip label="Undo last removal" placement="top" isDisabled={!canUndo}>
              <IconButton
                aria-label="Undo last removal"
                icon={<Undo2 size={13} />}
                size="xs"
                variant="ghost"
                colorScheme="gray"
                isDisabled={!canUndo}
                onClick={handleUndo}
              />
            </Tooltip>
            <Badge colorScheme="purple" borderRadius="full" px={2}>
              {actions.length}
            </Badge>
          </HStack>
        </Flex>

        <VStack align="stretch" spacing={2}>
          {preview.map((action) => (
            <HStack key={action.id} spacing={2} align="center">
              <Box
                w="8px"
                h="8px"
                borderRadius="full"
                bg={action.type === 'cleanup' ? 'teal.400' : action.type === 'review' ? 'orange.400' : 'purple.400'}
                flexShrink={0}
              />
              <Text fontSize="xs" color="gray.700" flex={1} noOfLines={1}>
                {action.name}
              </Text>
            </HStack>
          ))}
        </VStack>

        {actions.length > 0 && (
          <Box
            as="button"
            mt={2}
            fontSize="xs"
            color="blue.500"
            onClick={onOpen}
            _hover={{ color: 'blue.600' }}
            style={{ background: 'none', border: 'none', outline: 'none', cursor: 'pointer', padding: 0 }}
          >
            View all &amp; manage ({actions.length})
          </Box>
        )}

        {actions.length === 0 && (
          <Text fontSize="xs" color="gray.400" mt={1}>
            No actions selected.
          </Text>
        )}
      </Box>

      <ActionsDetailModal
        isOpen={isOpen}
        onClose={onClose}
        actions={actions}
        onRemoveAction={handleRemoveAction}
        onUndo={handleUndo}
        canUndo={canUndo}
      />
    </>
  );
};
